import { getChromeDeclarativeNetRequest } from '../../chrome/wrappers';
import { youtubeRules } from './youtubeRules';
import { getRulesStore } from '../../storage/stores';
import { onSafeSearchChanged } from '../safeSearch';
import { isProtectionEnabled } from '../../protectionStatus';
import { isSafeNetworkDelegated } from '../../safeNetworks/safeNetworksDelegation';
import { isDelegationStartedOrNotLoggedInWithDelegation } from '../../delegation/delegation';

/**
 * Set initial YouTube safe mode and change everytime is needed
 */
export const setupYoutubeSafeMode = async () => {
  await updateYoutubeSafeMode();
  onSafeSearchChanged(updateYoutubeSafeMode);
};

/**
 * Updates session-scoped declarative net request rules.
 *
 * This function utilizes `declarativeNetRequest.updateSessionRules` instead
 * of `declarativeNetRequest.updateDynamicRules` due to observed
 * inconsistencies and issues with dynamic rules in certain browser versions: https://qustodio.atlassian.net/browse/CHR-311
 *
 * Session rules are applied only for the current browser session and are not
 * persisted.
 *
 * @param updateSessionRulesParams - The parameters specifying the rules to update.
 * This object conforms to the
 * `declarativeNetRequest.UpdateRuleOptions`
 * interface.
 * @returns Promise<void>
 */
const updateSessionRules = (
  updateSessionRulesParams: chrome.declarativeNetRequest.UpdateRuleOptions
) =>
  getChromeDeclarativeNetRequest().updateSessionRules(updateSessionRulesParams);

const getYoutubeRuleIds = () => youtubeRules().map(rule => rule.id);

const enableSafeMode = () =>
  updateSessionRules({
    removeRuleIds: getYoutubeRuleIds(),
    addRules: youtubeRules(),
  });

const disableSafeMode = () =>
  updateSessionRules({
    removeRuleIds: getYoutubeRuleIds(),
  });

export const updateYoutubeSafeMode = () =>
  shouldEnableSafeMode() ? enableSafeMode() : disableSafeMode();

const shouldEnableSafeMode = () =>
  getRulesStore().isSafeSearch() &&
  isProtectionEnabled() &&
  !isSafeNetworkDelegated() &&
  isDelegationStartedOrNotLoggedInWithDelegation();
